import { useState, useEffect, useRef } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Plus, Edit, Trash2, Save, X, LogOut, Package, Settings, ShoppingBag,
  Loader2, ArrowUpRight, Image as ImageIcon, Box, AlertCircle, GripVertical,
  FileText, Users, Layout, Home, ShoppingCart, MessageSquare, Menu, 
  ChevronDown, ChevronUp, Eye, EyeOff, Move
} from 'lucide-react';

import { cmsApi, customOrdersApi, ordersApi, productsApi, settingsApi, uploadsApi } from '../api/requests';

const emptyProduct = {
  title: '', slug: '', shortDescription: '', fullDescription: '',
  priceEUR: '', originalPrice: '', category: '', material: '', size: '',
  stockStatus: 'in_stock', images: [], glbModelUrl: '',
  featured: false, customOrderAvailable: false, isActive: true, sortOrder: 0
};

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [settings, setSettings] = useState({
    telegramUrl: '', instagramUrl: '', paypalClientId: '',
    paypalSecret: '', paypalSandbox: true, paypalCurrency: 'EUR'
  });
  const [cms, setCms] = useState(null);
  const [cmsSection, setCmsSection] = useState('header');
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const glbInputRef = useRef(null);

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, ordersRes, customOrdersRes, settingsRes, cmsRes] = await Promise.all([
        productsApi.getAllForAdmin(),
        ordersApi.getAdminOrders(),
        customOrdersApi.getAdminOrders(),
        settingsApi.getAdmin(),
        cmsApi.getAllForAdmin()
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setCustomOrders(customOrdersRes.data);
      setSettings(settingsRes.data);
      setCms(cmsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  // File upload handlers
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const currentCount = productForm.images.length;
    const allowedCount = 5 - currentCount;
    const filesToUpload = files.slice(0, allowedCount);
    if (!filesToUpload.length) { setError('Maximum 5 images allowed'); return; }
    setUploading(true);
    const uploadedUrls = [];
    try {
      for (const file of filesToUpload) {
        const { data } = await uploadsApi.uploadAdmin(file);
        uploadedUrls.push(data.url);
      }
      setProductForm(prev => ({ ...prev, images: [...prev.images, ...uploadedUrls] }));
    } catch (err) { setError('Failed to upload'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleGlbUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await uploadsApi.uploadAdmin(file);
      setProductForm(prev => ({ ...prev, glbModelUrl: data.url }));
    } catch (err) { setError('Failed to upload 3D model'); }
    finally { setUploading(false); if (glbInputRef.current) glbInputRef.current.value = ''; }
  };

  const removeImage = (index) => {
    setProductForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= productForm.images.length) return;
    const newImages = [...productForm.images];
    const [removed] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, removed);
    setProductForm(prev => ({ ...prev, images: newImages }));
  };

  // Product handlers
  const openCreateForm = () => { setIsCreating(true); setEditingProductId(null); setProductForm(emptyProduct); setError(''); };
  const openEditForm = (product) => {
    setIsCreating(false); setEditingProductId(product.id);
    setProductForm({
      title: product.title || '', slug: product.slug || '',
      shortDescription: product.shortDescription || '', fullDescription: product.fullDescription || '',
      priceEUR: product.priceEUR?.toString() || '', originalPrice: product.originalPrice?.toString() || '',
      category: product.category || '', material: product.material || '', size: product.size || '',
      stockStatus: product.stockStatus || 'in_stock', images: product.images || [],
      glbModelUrl: product.glbModelUrl || '', featured: product.featured || false,
      customOrderAvailable: product.customOrderAvailable || false, isActive: product.isActive !== false,
      sortOrder: product.sortOrder || 0
    });
    setError('');
  };
  const closeForm = () => { setIsCreating(false); setEditingProductId(null); setProductForm(emptyProduct); setError(''); };

  const handleSaveProduct = async () => {
    if (!productForm.title.trim()) { setError('Title is required'); return; }
    if (productForm.images.length === 0) { setError('At least one image is required'); return; }
    setSaving(true); setError('');
    try {
      const productData = {
        ...productForm,
        priceEUR: parseFloat(productForm.priceEUR) || 0,
        originalPrice: productForm.originalPrice ? parseFloat(productForm.originalPrice) : null,
        sortOrder: parseInt(productForm.sortOrder) || 0
      };
      if (editingProductId) {
        await productsApi.update(editingProductId, productData);
        setSuccess('Product updated');
      } else {
        await productsApi.create(productData);
        setSuccess('Product created');
      }
      await fetchData(); closeForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save product');
    } finally { setSaving(false); }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await productsApi.remove(productId);
      await fetchData(); setSuccess('Product deleted'); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError('Failed to delete product'); }
  };

  const handleUpdateOrderStatus = async (orderId, status, isCustom = false) => {
    try {
      if (isCustom) {
        await customOrdersApi.updateStatus(orderId, status);
      } else {
        await ordersApi.updateStatus(orderId, status);
      }
      await fetchData(); setSuccess('Status updated'); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError('Failed to update status'); }
  };

  const handleSaveSettings = async () => {
    setSaving(true); setError('');
    try {
      await settingsApi.update(settings);
      setSuccess('Settings saved'); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const handleSaveCms = async () => {
    setSaving(true); setError('');
    try {
      await cmsApi.update(cms);
      setSuccess('Content saved'); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError('Failed to save content'); }
    finally { setSaving(false); }
  };

  const updateCms = (section, key, value) => {
    setCms(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: value }
    }));
  };

  const updateCmsNested = (section, parent, key, value) => {
    setCms(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [parent]: { ...prev[section][parent], [key]: value }
      }
    }));
  };

  const handleLogout = async () => { await logout(); };

  if (authLoading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="w-8 h-8 text-indigo-400 animate-spin" /></div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const showForm = isCreating || editingProductId !== null;

  const cmsSections = [
    { id: 'header', label: 'Header', icon: Menu },
    { id: 'home', label: 'Home Page', icon: Home },
    { id: 'collection', label: 'Collection', icon: ShoppingCart },
    { id: 'customOrder', label: 'Custom Order', icon: Users },
    { id: 'faq', label: 'FAQ', icon: MessageSquare },
    { id: 'footer', label: 'Footer', icon: Layout },
    { id: 'product', label: 'Product Page', icon: Package },
    { id: 'checkout', label: 'Checkout', icon: ShoppingBag }
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-xl font-bold text-white font-['Outfit']">
                CREO<span className="text-indigo-400">FARM</span>
              </Link>
              <span className="text-xs bg-indigo-500/20 text-indigo-400 px-2 py-1 rounded">Admin</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/" target="_blank" className="flex items-center space-x-1 text-zinc-400 hover:text-white text-sm">
                <span>View Site</span><ArrowUpRight className="w-4 h-4" />
              </Link>
              <button onClick={handleLogout} data-testid="logout-btn" className="flex items-center space-x-2 text-zinc-400 hover:text-white text-sm">
                <LogOut className="w-4 h-4" /><span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="pt-16 flex">
        {/* Sidebar */}
        <aside className="fixed left-0 top-16 bottom-0 w-64 bg-[#121212] border-r border-white/10 p-4 overflow-y-auto">
          <nav className="space-y-2">
            {[
              { id: 'products', label: 'Products', icon: Package },
              { id: 'orders', label: 'Orders', icon: ShoppingBag, count: orders.length },
              { id: 'custom-orders', label: 'Custom Orders', icon: Users, count: customOrders.length },
              { id: 'content', label: 'Site Content', icon: FileText },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(item => (
              <button key={item.id} onClick={() => setActiveTab(item.id)} data-testid={`tab-${item.id}`}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === item.id ? 'bg-indigo-500/20 text-indigo-400' : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                }`}>
                <item.icon className="w-5 h-5" /><span>{item.label}</span>
                {item.count > 0 && <span className="ml-auto bg-indigo-500 text-white text-xs px-2 py-0.5 rounded-full">{item.count}</span>}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="ml-64 flex-1 p-8">
          {error && (
            <div className="mb-6 flex items-start space-x-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-red-400">{error}</span>
              <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>
            </div>
          )}
          {success && (
            <div className="mb-6 flex items-center space-x-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <span className="text-emerald-400">{success}</span>
            </div>
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <ProductsTab
              products={products} loading={loading} showForm={showForm}
              isCreating={isCreating} editingProductId={editingProductId}
              productForm={productForm} setProductForm={setProductForm}
              uploading={uploading} saving={saving}
              fileInputRef={fileInputRef} glbInputRef={glbInputRef}
              openCreateForm={openCreateForm} openEditForm={openEditForm}
              closeForm={closeForm} handleSaveProduct={handleSaveProduct}
              handleDeleteProduct={handleDeleteProduct}
              handleFileUpload={handleFileUpload} handleGlbUpload={handleGlbUpload}
              removeImage={removeImage} moveImage={moveImage}
            />
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <OrdersTab orders={orders} loading={loading} handleUpdateOrderStatus={handleUpdateOrderStatus} />
          )}

          {/* Custom Orders Tab */}
          {activeTab === 'custom-orders' && (
            <CustomOrdersTab orders={customOrders} loading={loading} handleUpdateOrderStatus={handleUpdateOrderStatus} />
          )}

          {/* Content Tab - Full CMS */}
          {activeTab === 'content' && cms && (
            <div data-testid="content-tab">
              <div className="flex items-center justify-between mb-8">
                <h1 className="text-2xl font-['Outfit'] font-medium text-white">Site Content</h1>
                <button onClick={handleSaveCms} disabled={saving}
                  className="flex items-center space-x-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg transition-colors">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span>Save All Content</span>
                </button>
              </div>

              <div className="flex gap-6">
                {/* CMS Section Tabs */}
                <div className="w-48 flex-shrink-0">
                  <div className="bg-[#121212] border border-white/10 rounded-xl p-2 space-y-1 sticky top-24">
                    {cmsSections.map(section => (
                      <button key={section.id} onClick={() => setCmsSection(section.id)}
                        className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          cmsSection === section.id ? 'bg-indigo-500/20 text-indigo-400' : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                        }`}>
                        <section.icon className="w-4 h-4" /><span>{section.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* CMS Editor */}
                <div className="flex-1 max-w-4xl">
                  {cmsSection === 'header' && <HeaderCmsEditor cms={cms} setCms={setCms} />}
                  {cmsSection === 'home' && <HomeCmsEditor cms={cms} setCms={setCms} />}
                  {cmsSection === 'collection' && <CollectionCmsEditor cms={cms} setCms={setCms} />}
                  {cmsSection === 'customOrder' && <CustomOrderCmsEditor cms={cms} setCms={setCms} />}
                  {cmsSection === 'faq' && <FaqCmsEditor cms={cms} setCms={setCms} />}
                  {cmsSection === 'footer' && <FooterCmsEditor cms={cms} setCms={setCms} />}
                  {cmsSection === 'product' && <ProductCmsEditor cms={cms} setCms={setCms} />}
                  {cmsSection === 'checkout' && <CheckoutCmsEditor cms={cms} setCms={setCms} />}
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <SettingsTab settings={settings} setSettings={setSettings} saving={saving} handleSaveSettings={handleSaveSettings} />
          )}
        </main>
      </div>
    </div>
  );
}

// CMS Editor Components
function CmsField({ label, value, onChange, type = 'text', placeholder = '', multiline = false, rows = 3 }) {
  return (
    <div>
      <label className="block text-sm text-zinc-400 mb-2">{label}</label>
      {multiline ? (
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder}
          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-indigo-500 outline-none resize-none" />
      ) : (
        <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-indigo-500 outline-none" />
      )}
    </div>
  );
}

function CmsSection({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-[#121212] border border-white/10 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/5 transition-colors">
        <h3 className="text-lg font-medium text-white">{title}</h3>
        {open ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
      </button>
      {open && <div className="px-6 pb-6 space-y-4">{children}</div>}
    </div>
  );
}

function HeaderCmsEditor({ cms, setCms }) {
  const updateHeader = (key, value) => setCms(prev => ({ ...prev, header: { ...prev.header, [key]: value } }));
  const updateNavItem = (index, key, value) => {
    const items = [...cms.header.navItems];
    items[index] = { ...items[index], [key]: value };
    updateHeader('navItems', items);
  };
  const addNavItem = () => {
    const items = [...cms.header.navItems, { id: `nav-${Date.now()}`, label: 'New Link', path: '/', visible: true, order: cms.header.navItems.length + 1 }];
    updateHeader('navItems', items);
  };
  const removeNavItem = (index) => {
    updateHeader('navItems', cms.header.navItems.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <CmsSection title="Logo">
        <div className="grid grid-cols-2 gap-4">
          <CmsField label="Logo Text (Main)" value={cms.header.logoText} onChange={(v) => updateHeader('logoText', v)} placeholder="CREO" />
          <CmsField label="Logo Text (Accent)" value={cms.header.logoAccent} onChange={(v) => updateHeader('logoAccent', v)} placeholder="FARM" />
        </div>
        <CmsField label="Logo Image URL (optional)" value={cms.header.logoImage} onChange={(v) => updateHeader('logoImage', v)} placeholder="Leave empty to use text logo" />
      </CmsSection>

      <CmsSection title="Navigation Menu">
        <div className="space-y-3">
          {cms.header.navItems.map((item, index) => (
            <div key={item.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
              <GripVertical className="w-4 h-4 text-zinc-500" />
              <input type="text" value={item.label} onChange={(e) => updateNavItem(index, 'label', e.target.value)}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm focus:border-indigo-500 outline-none" placeholder="Label" />
              <input type="text" value={item.path} onChange={(e) => updateNavItem(index, 'path', e.target.value)}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm focus:border-indigo-500 outline-none" placeholder="/path" />
              <button onClick={() => updateNavItem(index, 'visible', !item.visible)} className={`p-2 rounded ${item.visible ? 'text-emerald-400' : 'text-zinc-500'}`}>
                {item.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => removeNavItem(index)} className="p-2 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={addNavItem} className="flex items-center space-x-2 text-indigo-400 hover:text-indigo-300 text-sm">
            <Plus className="w-4 h-4" /><span>Add Menu Item</span>
          </button>
        </div>
      </CmsSection>

      <CmsSection title="CTA Button">
        <div className="grid grid-cols-2 gap-4">
          <CmsField label="Button Text" value={cms.header.ctaButton?.text} onChange={(v) => setCms(prev => ({ ...prev, header: { ...prev.header, ctaButton: { ...prev.header.ctaButton, text: v } } }))} />
          <CmsField label="Button Link" value={cms.header.ctaButton?.path} onChange={(v) => setCms(prev => ({ ...prev, header: { ...prev.header, ctaButton: { ...prev.header.ctaButton, path: v } } }))} />
        </div>
        <label className="flex items-center space-x-3 cursor-pointer">
          <input type="checkbox" checked={cms.header.ctaButton?.visible !== false}
            onChange={(e) => setCms(prev => ({ ...prev, header: { ...prev.header, ctaButton: { ...prev.header.ctaButton, visible: e.target.checked } } }))}
            className="w-5 h-5 rounded border-white/10 bg-white/5 text-indigo-500" />
          <span className="text-white">Show CTA Button</span>
        </label>
        <label className="flex items-center space-x-3 cursor-pointer">
          <input type="checkbox" checked={cms.header.showSocialIcons !== false}
            onChange={(e) => updateHeader('showSocialIcons', e.target.checked)}
            className="w-5 h-5 rounded border-white/10 bg-white/5 text-indigo-500" />
          <span className="text-white">Show Social Icons in Header</span>
        </label>
      </CmsSection>
    </div>
  );
}

function HomeCmsEditor({ cms, setCms }) {
  const updateHome = (section, key, value) => {
    setCms(prev => ({ ...prev, home: { ...prev.home, [section]: { ...prev.home[section], [key]: value } } }));
  };

  return (
    <div className="space-y-6">
      <CmsSection title="Hero Section">
        <CmsField label="Badge Text" value={cms.home.hero.badge} onChange={(v) => updateHome('hero', 'badge', v)} />
        <div className="grid grid-cols-2 gap-4">
          <CmsField label="Title Line 1" value={cms.home.hero.title} onChange={(v) => updateHome('hero', 'title', v)} />
          <CmsField label="Title Line 2 (Accent)" value={cms.home.hero.titleAccent} onChange={(v) => updateHome('hero', 'titleAccent', v)} />
        </div>
        <CmsField label="Subtitle" value={cms.home.hero.subtitle} onChange={(v) => updateHome('hero', 'subtitle', v)} multiline rows={2} />
        <div className="grid grid-cols-2 gap-4">
          <CmsField label="Primary Button Text" value={cms.home.hero.primaryButton?.text} onChange={(v) => setCms(prev => ({ ...prev, home: { ...prev.home, hero: { ...prev.home.hero, primaryButton: { ...prev.home.hero.primaryButton, text: v } } } }))} />
          <CmsField label="Primary Button Link" value={cms.home.hero.primaryButton?.path} onChange={(v) => setCms(prev => ({ ...prev, home: { ...prev.home, hero: { ...prev.home.hero, primaryButton: { ...prev.home.hero.primaryButton, path: v } } } }))} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <CmsField label="Secondary Button Text" value={cms.home.hero.secondaryButton?.text} onChange={(v) => setCms(prev => ({ ...prev, home: { ...prev.home, hero: { ...prev.home.hero, secondaryButton: { ...prev.home.hero.secondaryButton, text: v } } } }))} />
          <CmsField label="Secondary Button Link" value={cms.home.hero.secondaryButton?.path} onChange={(v) => setCms(prev => ({ ...prev, home: { ...prev.home, hero: { ...prev.home.hero, secondaryButton: { ...prev.home.hero.secondaryButton, path: v } } } }))} />
        </div>
        <CmsField label="Hero Image URL" value={cms.home.hero.heroImage} onChange={(v) => updateHome('hero', 'heroImage', v)} />
      </CmsSection>

      <CmsSection title="Featured Section">
        <CmsField label="Badge Text" value={cms.home.featured.badge} onChange={(v) => updateHome('featured', 'badge', v)} />
        <CmsField label="Title" value={cms.home.featured.title} onChange={(v) => updateHome('featured', 'title', v)} />
        <CmsField label="View All Link Text" value={cms.home.featured.viewAllText} onChange={(v) => updateHome('featured', 'viewAllText', v)} />
      </CmsSection>

      <CmsSection title="About Section">
        <CmsField label="Badge Text" value={cms.home.about.badge} onChange={(v) => updateHome('about', 'badge', v)} />
        <div className="grid grid-cols-2 gap-4">
          <CmsField label="Title Line 1" value={cms.home.about.title} onChange={(v) => updateHome('about', 'title', v)} />
          <CmsField label="Title Line 2 (Accent)" value={cms.home.about.titleAccent} onChange={(v) => updateHome('about', 'titleAccent', v)} />
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Paragraphs</label>
          {cms.home.about.paragraphs?.map((p, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <textarea value={p} onChange={(e) => {
                const paras = [...cms.home.about.paragraphs];
                paras[i] = e.target.value;
                updateHome('about', 'paragraphs', paras);
              }} rows={2} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm resize-none focus:border-indigo-500 outline-none" />
              <button onClick={() => updateHome('about', 'paragraphs', cms.home.about.paragraphs.filter((_, idx) => idx !== i))} className="p-2 text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => updateHome('about', 'paragraphs', [...(cms.home.about.paragraphs || []), ''])} className="text-indigo-400 text-sm flex items-center space-x-1">
            <Plus className="w-4 h-4" /><span>Add Paragraph</span>
          </button>
        </div>
      </CmsSection>

      <CmsSection title="Contact Section">
        <CmsField label="Badge Text" value={cms.home.contact.badge} onChange={(v) => updateHome('contact', 'badge', v)} />
        <CmsField label="Title" value={cms.home.contact.title} onChange={(v) => updateHome('contact', 'title', v)} />
        <CmsField label="Subtitle" value={cms.home.contact.subtitle} onChange={(v) => updateHome('contact', 'subtitle', v)} multiline rows={2} />
        <div className="grid grid-cols-2 gap-4">
          <CmsField label="Telegram Button Text" value={cms.home.contact.telegramButtonText} onChange={(v) => updateHome('contact', 'telegramButtonText', v)} />
          <CmsField label="Instagram Button Text" value={cms.home.contact.instagramButtonText} onChange={(v) => updateHome('contact', 'instagramButtonText', v)} />
        </div>
      </CmsSection>
    </div>
  );
}

function CollectionCmsEditor({ cms, setCms }) {
  const update = (key, value) => setCms(prev => ({ ...prev, collection: { ...prev.collection, [key]: value } }));
  return (
    <div className="space-y-6">
      <CmsSection title="Collection Page">
        <CmsField label="Badge Text" value={cms.collection.badge} onChange={(v) => update('badge', v)} />
        <CmsField label="Title" value={cms.collection.title} onChange={(v) => update('title', v)} />
        <CmsField label="Subtitle" value={cms.collection.subtitle} onChange={(v) => update('subtitle', v)} multiline rows={2} />
        <CmsField label="Search Placeholder" value={cms.collection.searchPlaceholder} onChange={(v) => update('searchPlaceholder', v)} />
        <CmsField label="Filter 'All' Text" value={cms.collection.filterAllText} onChange={(v) => update('filterAllText', v)} />
        <CmsField label="No Results Text" value={cms.collection.noResultsText} onChange={(v) => update('noResultsText', v)} />
        <CmsField label="Clear Filters Text" value={cms.collection.clearFiltersText} onChange={(v) => update('clearFiltersText', v)} />
      </CmsSection>
    </div>
  );
}

function CustomOrderCmsEditor({ cms, setCms }) {
  const update = (key, value) => setCms(prev => ({ ...prev, customOrder: { ...prev.customOrder, [key]: value } }));
  const updateNested = (section, key, value) => setCms(prev => ({ ...prev, customOrder: { ...prev.customOrder, [section]: { ...prev.customOrder[section], [key]: value } } }));

  return (
    <div className="space-y-6">
      <CmsSection title="Page Header">
        <CmsField label="Badge Text" value={cms.customOrder.badge} onChange={(v) => update('badge', v)} />
        <CmsField label="Title" value={cms.customOrder.title} onChange={(v) => update('title', v)} />
        <CmsField label="Subtitle" value={cms.customOrder.subtitle} onChange={(v) => update('subtitle', v)} multiline rows={2} />
      </CmsSection>

      <CmsSection title="Upload Section">
        <CmsField label="Section Title" value={cms.customOrder.uploadSection?.title} onChange={(v) => updateNested('uploadSection', 'title', v)} />
        <CmsField label="Button Text" value={cms.customOrder.uploadSection?.buttonText} onChange={(v) => updateNested('uploadSection', 'buttonText', v)} />
        <CmsField label="Accept Text" value={cms.customOrder.uploadSection?.acceptText} onChange={(v) => updateNested('uploadSection', 'acceptText', v)} />
        <CmsField label="Change Image Text" value={cms.customOrder.uploadSection?.changeImageText} onChange={(v) => updateNested('uploadSection', 'changeImageText', v)} />
      </CmsSection>

      <CmsSection title="Size Selection & Pricing">
        <CmsField label="Section Title" value={cms.customOrder.sizeSection?.title} onChange={(v) => updateNested('sizeSection', 'title', v)} />
        {cms.customOrder.sizeSection?.sizes?.map((size, i) => (
          <div key={size.id} className="grid grid-cols-4 gap-3 p-3 bg-white/5 rounded-lg">
            <CmsField label="Label" value={size.label} onChange={(v) => {
              const sizes = [...cms.customOrder.sizeSection.sizes];
              sizes[i] = { ...sizes[i], label: v };
              updateNested('sizeSection', 'sizes', sizes);
            }} />
            <CmsField label="Size" value={size.size} onChange={(v) => {
              const sizes = [...cms.customOrder.sizeSection.sizes];
              sizes[i] = { ...sizes[i], size: v };
              updateNested('sizeSection', 'sizes', sizes);
            }} />
            <CmsField label="Price (€)" type="number" value={size.price} onChange={(v) => {
              const sizes = [...cms.customOrder.sizeSection.sizes];
              sizes[i] = { ...sizes[i], price: parseFloat(v) || 0 };
              updateNested('sizeSection', 'sizes', sizes);
            }} />
          </div>
        ))}
      </CmsSection>

      <CmsSection title="Submit Section">
        <CmsField label="Total Label" value={cms.customOrder.submitSection?.totalLabel} onChange={(v) => updateNested('submitSection', 'totalLabel', v)} />
        <CmsField label="Submit Button Text" value={cms.customOrder.submitSection?.buttonText} onChange={(v) => updateNested('submitSection', 'buttonText', v)} />
      </CmsSection>

      <CmsSection title="Success Page">
        <CmsField label="Title" value={cms.customOrder.successPage?.title} onChange={(v) => updateNested('successPage', 'title', v)} />
        <CmsField label="Message" value={cms.customOrder.successPage?.message} onChange={(v) => updateNested('successPage', 'message', v)} multiline />
        <CmsField label="Next Steps Title" value={cms.customOrder.successPage?.nextStepsTitle} onChange={(v) => updateNested('successPage', 'nextStepsTitle', v)} />
        <CmsField label="Next Steps Message" value={cms.customOrder.successPage?.nextStepsMessage} onChange={(v) => updateNested('successPage', 'nextStepsMessage', v)} multiline />
      </CmsSection>
    </div>
  );
}

function FaqCmsEditor({ cms, setCms }) {
  const update = (key, value) => setCms(prev => ({ ...prev, faq: { ...prev.faq, [key]: value } }));
  const updateItem = (index, key, value) => {
    const items = [...cms.faq.items];
    items[index] = { ...items[index], [key]: value };
    update('items', items);
  };
  const addItem = () => update('items', [...cms.faq.items, { question: '', answer: '' }]);
  const removeItem = (index) => update('items', cms.faq.items.filter((_, i) => i !== index));
  const moveItem = (from, to) => {
    if (to < 0 || to >= cms.faq.items.length) return;
    const items = [...cms.faq.items];
    const [removed] = items.splice(from, 1);
    items.splice(to, 0, removed);
    update('items', items);
  };

  return (
    <div className="space-y-6">
      <CmsSection title="FAQ Page Header">
        <CmsField label="Badge Text" value={cms.faq.badge} onChange={(v) => update('badge', v)} />
        <CmsField label="Title" value={cms.faq.title} onChange={(v) => update('title', v)} />
      </CmsSection>

      <CmsSection title="FAQ Items">
        <div className="space-y-4">
          {cms.faq.items?.map((item, i) => (
            <div key={i} className="p-4 bg-white/5 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">FAQ #{i + 1}</span>
                <div className="flex items-center space-x-2">
                  <button onClick={() => moveItem(i, i - 1)} disabled={i === 0} className="p-1 text-zinc-400 hover:text-white disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => moveItem(i, i + 1)} disabled={i === cms.faq.items.length - 1} className="p-1 text-zinc-400 hover:text-white disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                  <button onClick={() => removeItem(i)} className="p-1 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <input type="text" value={item.question} onChange={(e) => updateItem(i, 'question', e.target.value)} placeholder="Question"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm focus:border-indigo-500 outline-none" />
              <textarea value={item.answer} onChange={(e) => updateItem(i, 'answer', e.target.value)} placeholder="Answer" rows={3}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm resize-none focus:border-indigo-500 outline-none" />
            </div>
          ))}
          <button onClick={addItem} className="flex items-center space-x-2 text-indigo-400 hover:text-indigo-300 text-sm">
            <Plus className="w-4 h-4" /><span>Add FAQ Item</span>
          </button>
        </div>
      </CmsSection>
    </div>
  );
}

function FooterCmsEditor({ cms, setCms }) {
  const update = (key, value) => setCms(prev => ({ ...prev, footer: { ...prev.footer, [key]: value } }));

  return (
    <div className="space-y-6">
      <CmsSection title="Brand Section">
        <CmsField label="Brand Description" value={cms.footer.brandDescription} onChange={(v) => update('brandDescription', v)} multiline rows={2} />
      </CmsSection>

      <CmsSection title="Quick Links">
        <CmsField label="Section Title" value={cms.footer.quickLinksTitle} onChange={(v) => update('quickLinksTitle', v)} />
        {cms.footer.quickLinks?.map((link, i) => (
          <div key={i} className="flex gap-3">
            <input type="text" value={link.label} onChange={(e) => {
              const links = [...cms.footer.quickLinks];
              links[i] = { ...links[i], label: e.target.value };
              update('quickLinks', links);
            }} placeholder="Label" className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm focus:border-indigo-500 outline-none" />
            <input type="text" value={link.path} onChange={(e) => {
              const links = [...cms.footer.quickLinks];
              links[i] = { ...links[i], path: e.target.value };
              update('quickLinks', links);
            }} placeholder="/path" className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm focus:border-indigo-500 outline-none" />
            <button onClick={() => update('quickLinks', cms.footer.quickLinks.filter((_, idx) => idx !== i))} className="p-2 text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => update('quickLinks', [...(cms.footer.quickLinks || []), { label: '', path: '' }])} className="text-indigo-400 text-sm flex items-center space-x-1">
          <Plus className="w-4 h-4" /><span>Add Link</span>
        </button>
      </CmsSection>

      <CmsSection title="Legal Links">
        <CmsField label="Section Title" value={cms.footer.legalTitle} onChange={(v) => update('legalTitle', v)} />
        {cms.footer.legalLinks?.map((link, i) => (
          <div key={i} className="flex gap-3">
            <input type="text" value={link.label} onChange={(e) => {
              const links = [...cms.footer.legalLinks];
              links[i] = { ...links[i], label: e.target.value };
              update('legalLinks', links);
            }} placeholder="Label" className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm focus:border-indigo-500 outline-none" />
            <input type="text" value={link.path} onChange={(e) => {
              const links = [...cms.footer.legalLinks];
              links[i] = { ...links[i], path: e.target.value };
              update('legalLinks', links);
            }} placeholder="/path" className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm focus:border-indigo-500 outline-none" />
            <button onClick={() => update('legalLinks', cms.footer.legalLinks.filter((_, idx) => idx !== i))} className="p-2 text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </CmsSection>

      <CmsSection title="Copyright">
        <CmsField label="Copyright Text" value={cms.footer.copyrightText} onChange={(v) => update('copyrightText', v)} placeholder="Use {year} for dynamic year" />
        <CmsField label="Tagline" value={cms.footer.tagline} onChange={(v) => update('tagline', v)} />
      </CmsSection>
    </div>
  );
}

function ProductCmsEditor({ cms, setCms }) {
  const update = (key, value) => setCms(prev => ({ ...prev, product: { ...prev.product, [key]: value } }));
  return (
    <div className="space-y-6">
      <CmsSection title="Product Page Labels">
        <div className="grid grid-cols-2 gap-4">
          <CmsField label="Back Button" value={cms.product.backButtonText} onChange={(v) => update('backButtonText', v)} />
          <CmsField label="Price Label" value={cms.product.priceLabel} onChange={(v) => update('priceLabel', v)} />
          <CmsField label="Material Label" value={cms.product.materialLabel} onChange={(v) => update('materialLabel', v)} />
          <CmsField label="Size Label" value={cms.product.sizeLabel} onChange={(v) => update('sizeLabel', v)} />
          <CmsField label="Description Title" value={cms.product.descriptionTitle} onChange={(v) => update('descriptionTitle', v)} />
          <CmsField label="Buy Button Text" value={cms.product.buyButtonText} onChange={(v) => update('buyButtonText', v)} />
          <CmsField label="Related Products Title" value={cms.product.relatedTitle} onChange={(v) => update('relatedTitle', v)} />
          <CmsField label="Savings Text" value={cms.product.savingsText} onChange={(v) => update('savingsText', v)} />
        </div>
      </CmsSection>

      <CmsSection title="Sold Out State">
        <CmsField label="Sold Out Message" value={cms.product.soldOutText} onChange={(v) => update('soldOutText', v)} />
        <CmsField label="Notify Button Text" value={cms.product.notifyButtonText} onChange={(v) => update('notifyButtonText', v)} />
      </CmsSection>

      <CmsSection title="Custom Order">
        <CmsField label="Custom Order Text" value={cms.product.customOrderText} onChange={(v) => update('customOrderText', v)} />
        <CmsField label="Custom Order Button" value={cms.product.customOrderButtonText} onChange={(v) => update('customOrderButtonText', v)} />
      </CmsSection>

      <CmsSection title="Social Buttons">
        <div className="grid grid-cols-2 gap-4">
          <CmsField label="Telegram Button" value={cms.product.telegramButtonText} onChange={(v) => update('telegramButtonText', v)} />
          <CmsField label="Instagram Button" value={cms.product.instagramButtonText} onChange={(v) => update('instagramButtonText', v)} />
        </div>
      </CmsSection>
    </div>
  );
}

function CheckoutCmsEditor({ cms, setCms }) {
  const update = (key, value) => setCms(prev => ({ ...prev, checkout: { ...prev.checkout, [key]: value } }));
  const updateNested = (section, key, value) => setCms(prev => ({ ...prev, checkout: { ...prev.checkout, [section]: { ...prev.checkout[section], [key]: value } } }));

  return (
    <div className="space-y-6">
      <CmsSection title="Form Labels">
        <div className="grid grid-cols-2 gap-4">
          <CmsField label="Shipping Title" value={cms.checkout.shippingTitle} onChange={(v) => update('shippingTitle', v)} />
          <CmsField label="Address Title" value={cms.checkout.addressTitle} onChange={(v) => update('addressTitle', v)} />
          <CmsField label="Name Label" value={cms.checkout.nameLabel} onChange={(v) => update('nameLabel', v)} />
          <CmsField label="Email Label" value={cms.checkout.emailLabel} onChange={(v) => update('emailLabel', v)} />
          <CmsField label="Phone Label" value={cms.checkout.phoneLabel} onChange={(v) => update('phoneLabel', v)} />
          <CmsField label="Country Label" value={cms.checkout.countryLabel} onChange={(v) => update('countryLabel', v)} />
          <CmsField label="City Label" value={cms.checkout.cityLabel} onChange={(v) => update('cityLabel', v)} />
          <CmsField label="Street Label" value={cms.checkout.streetLabel} onChange={(v) => update('streetLabel', v)} />
          <CmsField label="Postal Label" value={cms.checkout.postalLabel} onChange={(v) => update('postalLabel', v)} />
          <CmsField label="Apartment Label" value={cms.checkout.apartmentLabel} onChange={(v) => update('apartmentLabel', v)} />
        </div>
        <CmsField label="Notes Label" value={cms.checkout.notesLabel} onChange={(v) => update('notesLabel', v)} />
        <CmsField label="Proceed Button Text" value={cms.checkout.proceedButtonText} onChange={(v) => update('proceedButtonText', v)} />
      </CmsSection>

      <CmsSection title="Success Page">
        <CmsField label="Title" value={cms.checkout.successPage?.title} onChange={(v) => updateNested('successPage', 'title', v)} />
        <CmsField label="Message" value={cms.checkout.successPage?.message} onChange={(v) => updateNested('successPage', 'message', v)} multiline />
        <CmsField label="Next Steps Title" value={cms.checkout.successPage?.nextStepsTitle} onChange={(v) => updateNested('successPage', 'nextStepsTitle', v)} />
        <CmsField label="Next Steps Message" value={cms.checkout.successPage?.nextStepsMessage} onChange={(v) => updateNested('successPage', 'nextStepsMessage', v)} multiline />
        <CmsField label="Continue Button" value={cms.checkout.successPage?.continueButtonText} onChange={(v) => updateNested('successPage', 'continueButtonText', v)} />
      </CmsSection>

      <CmsSection title="Cancel Page">
        <CmsField label="Title" value={cms.checkout.cancelPage?.title} onChange={(v) => updateNested('cancelPage', 'title', v)} />
        <CmsField label="Message" value={cms.checkout.cancelPage?.message} onChange={(v) => updateNested('cancelPage', 'message', v)} multiline />
        <CmsField label="Help Text" value={cms.checkout.cancelPage?.helpText} onChange={(v) => updateNested('cancelPage', 'helpText', v)} />
        <CmsField label="Try Again Button" value={cms.checkout.cancelPage?.tryAgainButtonText} onChange={(v) => updateNested('cancelPage', 'tryAgainButtonText', v)} />
      </CmsSection>
    </div>
  );
}

// Other Tab Components (simplified for space)
function ProductsTab({ products, loading, showForm, isCreating, editingProductId, productForm, setProductForm, uploading, saving, fileInputRef, glbInputRef, openCreateForm, openEditForm, closeForm, handleSaveProduct, handleDeleteProduct, handleFileUpload, handleGlbUpload, removeImage, moveImage }) {
  return (
    <div data-testid="products-tab">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-['Outfit'] font-medium text-white">Products</h1>
        <button onClick={openCreateForm} data-testid="add-product-btn" className="flex items-center space-x-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors">
          <Plus className="w-4 h-4" /><span>Add Product</span>
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-[#121212] border border-white/10 rounded-2xl">
            <div className="sticky top-0 bg-[#121212] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-['Outfit'] font-medium text-white">{isCreating ? 'Create New Product' : 'Edit Product'}</h2>
              <button onClick={closeForm} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-6">
              {/* Product form fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm text-zinc-400 mb-2">Title *</label>
                  <input type="text" value={productForm.title} onChange={(e) => setProductForm({ ...productForm, title: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none" placeholder="Product title" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Category</label>
                  <input type="text" value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Stock Status</label>
                  <select value={productForm.stockStatus} onChange={(e) => setProductForm({ ...productForm, stockStatus: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none">
                    <option value="in_stock">In Stock</option><option value="made_to_order">Made to Order</option><option value="sold_out">Sold Out</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Short Description</label>
                <input type="text" value={productForm.shortDescription} onChange={(e) => setProductForm({ ...productForm, shortDescription: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Full Description</label>
                <textarea value={productForm.fullDescription} onChange={(e) => setProductForm({ ...productForm, fullDescription: e.target.value })} rows={4}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none resize-none" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Price (EUR) *</label>
                  <input type="number" step="0.01" value={productForm.priceEUR} onChange={(e) => setProductForm({ ...productForm, priceEUR: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Original Price</label>
                  <input type="number" step="0.01" value={productForm.originalPrice} onChange={(e) => setProductForm({ ...productForm, originalPrice: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none" placeholder="For discount" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Material</label>
                  <input type="text" value={productForm.material} onChange={(e) => setProductForm({ ...productForm, material: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none" />
                </div>
              </div>
              {/* Images */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Images * (max 5, first = main)</label>
                <div className="grid grid-cols-5 gap-3 mb-3">
                  {productForm.images.map((img, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img src={img} alt="" className={`w-full h-full object-cover rounded-lg ${index === 0 ? 'ring-2 ring-indigo-500' : ''}`} />
                      {index === 0 && <span className="absolute top-1 left-1 bg-indigo-500 text-white text-xs px-1.5 py-0.5 rounded">Main</span>}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                        {index > 0 && <button onClick={() => moveImage(index, index - 1)} className="p-1 bg-white/20 rounded hover:bg-white/40"><GripVertical className="w-4 h-4 text-white rotate-90" /></button>}
                        <button onClick={() => removeImage(index)} className="p-1 bg-red-500/80 rounded hover:bg-red-500"><X className="w-4 h-4 text-white" /></button>
                      </div>
                    </div>
                  ))}
                  {productForm.images.length < 5 && (
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                      className="aspect-square border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center text-zinc-500 hover:border-indigo-500 hover:text-indigo-400 transition-colors">
                      {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><ImageIcon className="w-6 h-6 mb-1" /><span className="text-xs">Add</span></>}
                    </button>
                  )}
                </div>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" multiple className="hidden" />
              </div>
              {/* GLB */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">3D Model (GLB)</label>
                <div className="flex items-center space-x-4">
                  <input type="text" value={productForm.glbModelUrl} onChange={(e) => setProductForm({ ...productForm, glbModelUrl: e.target.value })}
                    className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none" placeholder="GLB URL" />
                  <input type="file" ref={glbInputRef} onChange={handleGlbUpload} accept=".glb,.gltf" className="hidden" />
                  <button onClick={() => glbInputRef.current?.click()} disabled={uploading}
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white hover:bg-white/10 flex items-center space-x-2">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Box className="w-4 h-4" />}<span>Upload</span>
                  </button>
                </div>
              </div>
              {/* Toggles */}
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center space-x-3 cursor-pointer p-3 bg-white/5 rounded-lg">
                  <input type="checkbox" checked={productForm.featured} onChange={(e) => setProductForm({ ...productForm, featured: e.target.checked })}
                    className="w-5 h-5 rounded border-white/10 bg-white/5 text-indigo-500" />
                  <span className="text-white">Featured</span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer p-3 bg-white/5 rounded-lg">
                  <input type="checkbox" checked={productForm.isActive} onChange={(e) => setProductForm({ ...productForm, isActive: e.target.checked })}
                    className="w-5 h-5 rounded border-white/10 bg-white/5 text-indigo-500" />
                  <span className="text-white">Active</span>
                </label>
              </div>
            </div>
            <div className="sticky bottom-0 bg-[#121212] border-t border-white/10 px-6 py-4 flex items-center justify-end space-x-4">
              <button onClick={closeForm} className="px-6 py-2 text-zinc-400 hover:text-white">Cancel</button>
              <button onClick={handleSaveProduct} disabled={saving} data-testid="save-product-btn"
                className="flex items-center space-x-2 px-6 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>{isCreating ? 'Create' : 'Save'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-indigo-400 animate-spin" /></div>
      ) : products.length === 0 ? (
        <div className="text-center py-24"><Package className="w-12 h-12 mx-auto mb-4 text-zinc-600" /><p className="text-zinc-400">No products yet</p></div>
      ) : (
        <div className="bg-[#121212] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left text-sm text-zinc-400 font-medium px-6 py-4">Product</th>
                <th className="text-left text-sm text-zinc-400 font-medium px-6 py-4">Category</th>
                <th className="text-left text-sm text-zinc-400 font-medium px-6 py-4">Price</th>
                <th className="text-left text-sm text-zinc-400 font-medium px-6 py-4">Status</th>
                <th className="text-right text-sm text-zinc-400 font-medium px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {products.map((product) => (
                <tr key={product.id} className={`hover:bg-white/5 ${!product.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-4">
                      <img src={product.thumbnailImage} alt={product.title} className="w-12 h-12 object-cover rounded-lg" />
                      <div>
                        <p className="text-white font-medium">{product.title}</p>
                        <div className="flex items-center space-x-2">
                          {product.featured && <span className="text-xs text-amber-400">Featured</span>}
                          {product.hasDiscount && <span className="text-xs text-emerald-400">-{product.discountPercent}%</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-zinc-400">{product.category}</td>
                  <td className="px-6 py-4">
                    <span className="text-white">€{product.priceEUR.toFixed(2)}</span>
                    {product.hasDiscount && <span className="ml-2 text-zinc-500 line-through text-sm">€{product.originalPrice.toFixed(2)}</span>}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-sm ${product.stockStatus === 'in_stock' ? 'text-emerald-400' : product.stockStatus === 'made_to_order' ? 'text-amber-400' : 'text-red-400'}`}>
                      {product.stockStatus.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button onClick={() => openEditForm(product)} className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteProduct(product.id)} className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OrdersTab({ orders, loading, handleUpdateOrderStatus }) {
  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-indigo-400 animate-spin" /></div>;
  return (
    <div data-testid="orders-tab">
      <h1 className="text-2xl font-['Outfit'] font-medium text-white mb-8">Orders</h1>
      {orders.length === 0 ? (
        <div className="text-center py-24 text-zinc-400"><ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No orders yet</p></div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} className="bg-[#121212] border border-white/10 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div><p className="text-white font-medium">{order.customerName}</p><p className="text-zinc-500 text-sm">{order.customerEmail}</p></div>
                <div className="text-right"><p className="text-white font-medium">€{order.amount.toFixed(2)}</p><p className="text-zinc-500 text-sm">{new Date(order.createdAt).toLocaleDateString()}</p></div>
              </div>
              {order.shippingStreet && (
                <div className="mb-4 p-3 bg-white/5 rounded-lg text-sm">
                  <p className="text-zinc-400 mb-1">Shipping:</p>
                  <p className="text-white">{order.shippingStreet}, {order.shippingPostalCode} {order.shippingCity}, {order.shippingCountry}</p>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-zinc-400 text-sm">Status:</span>
                  <select value={order.status} onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                    className="px-3 py-1 bg-white/5 border border-white/10 rounded text-white text-sm focus:border-indigo-500 outline-none">
                    <option value="pending">Pending</option><option value="paid">Paid</option><option value="processing">Processing</option>
                    <option value="shipped">Shipped</option><option value="delivered">Delivered</option><option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomOrdersTab({ orders, loading, handleUpdateOrderStatus }) {
  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-indigo-400 animate-spin" /></div>;
  return (
    <div data-testid="custom-orders-tab">
      <h1 className="text-2xl font-['Outfit'] font-medium text-white mb-8">Custom Orders</h1>
      {orders.length === 0 ? (
        <div className="text-center py-24 text-zinc-400"><Users className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No custom orders yet</p></div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} data-testid={`custom-order-${order.id}`} className="bg-[#121212] border border-white/10 rounded-xl p-6">
              <div className="flex flex-col gap-4">
                {/* Header row */}
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white font-medium">{order.customerName}</p>
                    <p className="text-zinc-500 text-sm">{order.customerEmail}</p>
                    {order.customerPhone && <p className="text-zinc-500 text-sm">{order.customerPhone}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-white font-medium text-lg">{order.price.toFixed(2)} EUR</p>
                    {order.paypalOrderId && <p className="text-emerald-400 text-xs font-mono mt-0.5">PayPal: {order.paypalOrderId}</p>}
                    <p className="text-zinc-500 text-xs mt-1">{new Date(order.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Images row */}
                {order.imageUrls?.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {order.imageUrls.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                        <img src={url} alt={`Custom order photo ${i + 1}`} className="w-20 h-20 object-cover rounded-lg border border-white/10 hover:border-indigo-500/50 transition-colors" />
                      </a>
                    ))}
                  </div>
                )}

                {/* Config details */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-zinc-500 text-xs">Type</p>
                    <p className="text-white capitalize">{order.figurineType}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-zinc-500 text-xs">Size</p>
                    <p className="text-white capitalize">{order.selectedSize}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-zinc-500 text-xs">Detail Level</p>
                    <p className="text-white capitalize">{order.complexityLevel}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-zinc-500 text-xs">Extras</p>
                    <p className="text-white">
                      {order.extraPersons > 0 ? `+${order.extraPersons} person${order.extraPersons > 1 ? 's' : ''}` : ''}
                      {order.addFrame ? (order.extraPersons > 0 ? ', Frame' : 'Frame') : ''}
                      {!order.extraPersons && !order.addFrame ? 'None' : ''}
                    </p>
                  </div>
                </div>

                {/* Price breakdown */}
                {order.priceBreakdown && (
                  <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 text-xs space-y-1">
                    <div className="flex justify-between"><span className="text-zinc-500">Base ({order.priceBreakdown.baseLabel})</span><span className="text-zinc-300">{order.priceBreakdown.base} EUR</span></div>
                    {order.priceBreakdown.sizeSurcharge > 0 && <div className="flex justify-between"><span className="text-zinc-500">Size ({order.priceBreakdown.sizeLabel})</span><span className="text-zinc-300">+{order.priceBreakdown.sizeSurcharge} EUR</span></div>}
                    {order.priceBreakdown.extraPersonsCost > 0 && <div className="flex justify-between"><span className="text-zinc-500">Extra persons</span><span className="text-zinc-300">+{order.priceBreakdown.extraPersonsCost} EUR</span></div>}
                    {order.priceBreakdown.frameCost > 0 && <div className="flex justify-between"><span className="text-zinc-500">Frame</span><span className="text-zinc-300">+{order.priceBreakdown.frameCost} EUR</span></div>}
                    {order.priceBreakdown.complexitySurcharge > 0 && <div className="flex justify-between"><span className="text-zinc-500">{order.priceBreakdown.complexityLabel}</span><span className="text-zinc-300">+{order.priceBreakdown.complexitySurcharge} EUR</span></div>}
                  </div>
                )}

                {/* Notes */}
                {order.notes && <p className="text-zinc-400 text-sm bg-white/[0.02] rounded-lg p-3 border border-white/[0.06]">"{order.notes}"</p>}

                {/* Shipping */}
                {order.shipping && (
                  <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 text-sm">
                    <p className="text-zinc-500 text-xs mb-2 uppercase tracking-wider">Shipping Address</p>
                    <p className="text-zinc-300">{order.shipping.street}{order.shipping.apartment ? `, ${order.shipping.apartment}` : ''}</p>
                    <p className="text-zinc-300">{order.shipping.postalCode} {order.shipping.city}</p>
                    <p className="text-zinc-300">{order.shipping.country}</p>
                    {order.shipping.notes && <p className="text-zinc-500 text-xs mt-1 italic">"{order.shipping.notes}"</p>}
                  </div>
                )}

                {/* Status + Payment badge */}
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center space-x-2">
                    <span className="text-zinc-400 text-sm">Status:</span>
                    <select value={order.status} onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value, true)}
                      data-testid={`custom-order-status-${order.id}`}
                      className="px-3 py-1 bg-white/5 border border-white/10 rounded text-white text-sm focus:border-indigo-500 outline-none">
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  {order.status === 'paid' && (
                    <span className="px-2.5 py-1 bg-emerald-500/15 text-emerald-400 text-xs font-medium rounded-full border border-emerald-500/20">Paid</span>
                  )}
                  {order.status === 'pending' && (
                    <span className="px-2.5 py-1 bg-amber-500/15 text-amber-400 text-xs font-medium rounded-full border border-amber-500/20">Pending Payment</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsTab({ settings, setSettings, saving, handleSaveSettings }) {
  return (
    <div data-testid="settings-tab">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-['Outfit'] font-medium text-white">Settings</h1>
        <button onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn"
          className="flex items-center space-x-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}<span>Save Settings</span>
        </button>
      </div>
      <div className="max-w-2xl space-y-8">
        <div className="bg-[#121212] border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-medium text-white mb-6">Social Links</h2>
          <div className="space-y-4">
            <div><label className="block text-sm text-zinc-400 mb-2">Telegram URL</label>
              <input type="url" value={settings.telegramUrl} onChange={(e) => setSettings({ ...settings, telegramUrl: e.target.value })} placeholder="https://t.me/username"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none" /></div>
            <div><label className="block text-sm text-zinc-400 mb-2">Instagram URL</label>
              <input type="url" value={settings.instagramUrl} onChange={(e) => setSettings({ ...settings, instagramUrl: e.target.value })} placeholder="https://instagram.com/username"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none" /></div>
          </div>
        </div>
        <div className="bg-[#121212] border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-medium text-white mb-2">PayPal Integration</h2>
          <p className="text-zinc-500 text-sm mb-6">Configure your PayPal credentials</p>
          <div className="space-y-4">
            <div><label className="block text-sm text-zinc-400 mb-2">Client ID</label>
              <input type="text" value={settings.paypalClientId} onChange={(e) => setSettings({ ...settings, paypalClientId: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none" /></div>
            <div><label className="block text-sm text-zinc-400 mb-2">Secret</label>
              <input type="password" value={settings.paypalSecret} onChange={(e) => setSettings({ ...settings, paypalSecret: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm text-zinc-400 mb-2">Currency</label>
                <select value={settings.paypalCurrency} onChange={(e) => setSettings({ ...settings, paypalCurrency: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none">
                  <option value="EUR">EUR</option><option value="USD">USD</option><option value="GBP">GBP</option>
                </select></div>
              <div><label className="block text-sm text-zinc-400 mb-2">Mode</label>
                <select value={settings.paypalSandbox ? 'sandbox' : 'live'} onChange={(e) => setSettings({ ...settings, paypalSandbox: e.target.value === 'sandbox' })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-indigo-500 outline-none">
                  <option value="sandbox">Sandbox (Testing)</option><option value="live">Live (Production)</option>
                </select></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
